import {
  createEntityAdapter,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";

import {
  FileToUpload,
  isUploadAbortedError,
  uploadFile,
  uploadFileToCollection,
  UploadingStatus,
  UploadPhase,
} from "../../api/media-library";
import { updateAsset } from "../../api/media-library/update-asset";
import { FileWithURL } from "../../api/media-library/types";
import { CollectionFile } from "../../shared/types/files";
import { ReduxRootState } from "../../types/redux-store";

export type { FileToUpload } from "../../api/media-library";

export interface UploadingFile {
  id: string;
  progress: {
    value: number;
    status: UploadingStatus;
  };
  upload: {
    loaded: number;
    total: number | null;
    value: number;
  };
  processing: {
    value: number;
  };
  phase: UploadPhase;
  batchId: string;
  collectionId: string;
  result?: {
    thumbnail: string;
  };
  name: string;
  type: FileToUpload["type"];
  // Holds the Blob for a picked file. Released once the bytes are uploaded, and
  // kept on a failed row so Retry can resend it.
  file?: FileToUpload;
  error?: unknown;
}

export type UploadingMode = "parallel" | "subsequent";

const featureKey = "mediaPickerUploads" as const;

const uploadingFilesAdapter = createEntityAdapter<UploadingFile, string>({
  selectId: (upload) => upload.id,
});

const uploadingFilesSlice = createSlice({
  name: featureKey,
  initialState: uploadingFilesAdapter.getInitialState(),
  reducers: {
    uploadsQueued(state, action: PayloadAction<UploadingFile[]>) {
      uploadingFilesAdapter.upsertMany(state, action.payload);
    },
    uploadProgressUpdated(
      state,
      action: PayloadAction<{
        id: string;
        progress: UploadingFile["progress"];
        phase?: Extract<UploadPhase, "uploading" | "processing" | "saving">;
        loaded?: number;
        total?: number | null;
      }>
    ) {
      const current = state.entities[action.payload.id];

      if (!current) {
        return;
      }

      const { progress, phase, loaded, total } = action.payload;
      const changes: Partial<UploadingFile> = { progress };

      if (phase) {
        changes.phase = phase;
      }

      const nextTotal = total === undefined ? current.upload.total : total;
      const nextLoaded = loaded ?? current.upload.loaded;
      const bytesValue =
        nextTotal && nextTotal > 0
          ? Math.min(100, (nextLoaded / nextTotal) * 100)
          : progress.value;

      changes.upload =
        phase === "processing" || phase === "saving"
          ? {
              loaded: nextTotal ?? nextLoaded,
              total: nextTotal,
              value: 100,
            }
          : {
              loaded: nextLoaded,
              total: nextTotal,
              // Remote sources report a download on a different scale before
              // the S3 bytes start, which would otherwise rewind the row.
              value: Math.max(current.upload.value, bytesValue),
            };

      if (progress.status === UploadingStatus.transcoding) {
        changes.processing = { value: progress.value };
      }

      uploadingFilesAdapter.updateOne(state, {
        id: action.payload.id,
        changes,
      });
    },
    uploadResultReceived(
      state,
      action: PayloadAction<{
        id: string;
        thumbnail: string;
      }>
    ) {
      uploadingFilesAdapter.updateOne(state, {
        id: action.payload.id,
        changes: {
          result: { thumbnail: action.payload.thumbnail },
        },
      });
    },
    uploadFailed(
      state,
      action: PayloadAction<{
        id: string;
        error: unknown;
      }>
    ) {
      uploadingFilesAdapter.updateOne(state, {
        id: action.payload.id,
        changes: {
          error: action.payload.error,
          phase: "failed",
          progress: {
            value: 100,
            status: UploadingStatus.error,
          },
        },
      });
    },
    uploadSucceeded(state, action: PayloadAction<string>) {
      uploadingFilesAdapter.updateOne(state, {
        id: action.payload,
        changes: {
          phase: "ready",
          processing: { value: 100 },
          progress: {
            value: 100,
            status: UploadingStatus.success,
          },
          // Holding the source would pin its Blob for the life of the page.
          file: undefined,
        },
      });
    },
    clearUploads(state, action: PayloadAction<string[] | undefined>) {
      if (!action.payload) {
        uploadingFilesAdapter.removeAll(state);
        return;
      }

      uploadingFilesAdapter.removeMany(state, action.payload);
    },
  },
});

export const uploadingFilesReducer = uploadingFilesSlice.reducer;
export const uploadingFilesActions = uploadingFilesSlice.actions;
export type UploadingFilesState = ReturnType<typeof uploadingFilesReducer>;

function getUploadsState(state: ReduxRootState) {
  return (
    (
      state as ReduxRootState & {
        [featureKey]?: ReturnType<typeof uploadingFilesReducer>;
      }
    )[featureKey] ?? uploadingFilesAdapter.getInitialState()
  );
}

const adapterSelectors = uploadingFilesAdapter.getSelectors(getUploadsState);

export const selectUploadingFileById = adapterSelectors.selectById;
export const selectAllUploadingFiles = adapterSelectors.selectAll;
const selectUploadingFileEntities = adapterSelectors.selectEntities;
const UPLOADS_CACHE_LIMIT = 32;
const uploadsByIdsCache = new Map<
  string,
  {
    ids: string[];
    uploads: Array<UploadingFile | undefined>;
    result: UploadingFile[];
  }
>();

export function getUploadsByIdsCacheKeysForTest(): string[] {
  return [...uploadsByIdsCache.keys()];
}

// The sweep below only runs when the selector is called again, which does not
// happen once the picker unmounts, so clearing rows drops them here too.
function purgeUploadsByIdsCache(ids?: string[]) {
  if (!ids) {
    uploadsByIdsCache.clear();
    return;
  }

  const cleared = new Set(ids);

  for (const [key, entry] of uploadsByIdsCache) {
    if (entry.ids.some((id) => cleared.has(id))) {
      uploadsByIdsCache.delete(key);
    }
  }
}

// Use this rather than the slice action so the cache cannot outlive the rows.
export function clearUploads(ids?: string[]) {
  purgeUploadsByIdsCache(ids);
  return uploadingFilesActions.clearUploads(ids);
}

export function selectUploadingFilesByIds(
  state: ReduxRootState,
  ids: string[]
): UploadingFile[] {
  const entities = selectUploadingFileEntities(state);
  const uploads = ids.map((id) => entities[id]);
  const cacheKey = ids.join("\0");
  const cached = uploadsByIdsCache.get(cacheKey);

  if (
    cached &&
    cached.ids.length === ids.length &&
    cached.ids.every((id, index) => id === ids[index]) &&
    cached.uploads.length === uploads.length &&
    cached.uploads.every((upload, index) => upload === uploads[index])
  ) {
    return cached.result;
  }

  const result = uploads.filter(Boolean) as UploadingFile[];

  // A dismissed batch is gone from the store, but its cached rows would still
  // hold whichever sources had not been released.
  for (const [key, entry] of uploadsByIdsCache) {
    if (key !== cacheKey && entry.ids.some((id) => !entities[id])) {
      uploadsByIdsCache.delete(key);
    }
  }

  if (uploadsByIdsCache.size >= UPLOADS_CACHE_LIMIT) {
    const oldestKey = uploadsByIdsCache.keys().next().value;

    if (oldestKey !== undefined) {
      uploadsByIdsCache.delete(oldestKey);
    }
  }

  uploadsByIdsCache.set(cacheKey, {
    ids: [...ids],
    uploads,
    result,
  });

  return result;
}

let lastId = 0;
function getUniqId() {
  return `${lastId++}`;
}

const UPLOAD_ERROR_BY_STATUS: Record<number, string> = {
  401: "Your session expired, please sign in again",
  403: "You do not have permission to upload here",
  413: "This file is too large",
  429: "Too many uploads at once, please try again",
};

// The generated client throws GeneratedSkedApiError, whose own message is the
// raw request failure, so the status is preferred over it.
function getUploadError(error: unknown): string {
  if (
    error &&
    typeof error === "object" &&
    "status" in error &&
    typeof error.status === "number" &&
    UPLOAD_ERROR_BY_STATUS[error.status]
  ) {
    return UPLOAD_ERROR_BY_STATUS[error.status];
  }

  if (
    error &&
    typeof error === "object" &&
    "response" in error &&
    error.response &&
    typeof error.response === "object" &&
    "data" in error.response &&
    error.response.data &&
    typeof error.response.data === "object" &&
    "message" in error.response.data &&
    typeof error.response.data.message === "string" &&
    error.response.data.message.trim()
  ) {
    return error.response.data.message;
  }

  if (typeof error === "string" && error.trim()) {
    return error;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Upload failed";
}

// Browsers cap connections per host at around six. Going wider just queues the
// requests in the browser, where nothing can report or cancel them.
const MAX_CONCURRENT_UPLOADS = 4;

async function runWithConcurrency<T>(
  items: T[],
  limit: number,
  run: (item: T) => Promise<unknown>
): Promise<void> {
  let next = 0;

  const worker = async () => {
    while (next < items.length) {
      const item = items[next++];
      await run(item);
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, () => worker())
  );
}

const abortControllers = new Map<string, AbortController>();

export function abortUploadById(id: string): boolean {
  const controller = abortControllers.get(id);

  if (!controller) {
    return false;
  }

  controller.abort();
  return true;
}

// Created when a file is queued, not when a worker picks it up, so a cancel
// that lands behind the concurrency limit stops the file from ever starting.
function createAbortController(id: string): AbortController {
  const controller = new AbortController();
  abortControllers.set(id, controller);
  return controller;
}

// The queue holds the second reference to each source. The store row holds the
// other, so releasing only that one frees nothing until the whole batch drains.
interface QueuedUpload {
  id: string;
  file?: FileToUpload;
  slot: number;
}

async function uploadSingleFile({
  collectionId,
  upload,
  dispatch,
  onFileUploaded,
}: {
  collectionId: string;
  upload: QueuedUpload;
  dispatch: (action: unknown) => unknown;
  onFileUploaded(uploaded: CollectionFile, slot: number): void;
}) {
  const controller =
    abortControllers.get(upload.id) ?? createAbortController(upload.id);
  const source = upload.file;

  if (!source || controller.signal.aborted) {
    abortControllers.delete(upload.id);
    dispatch(clearUploads([upload.id]));
    return;
  }

  try {
    const intermediate = await uploadFile(source, {
      signal: controller.signal,
      onProgress({ value, status, phase, loaded, total }) {
        dispatch(
          uploadingFilesActions.uploadProgressUpdated({
            id: upload.id,
            progress: {
              value,
              status,
            },
            phase,
            loaded,
            total,
          })
        );
      },
    });

    if (controller.signal.aborted) {
      dispatch(clearUploads([upload.id]));
      return;
    }

    const uploaded = await uploadFileToCollection({
      collectionId,
      file: { ...intermediate, name: source.name },
    });

    if ((source as FileWithURL).contentType === "url") {
      const fileWithURL = source as FileWithURL;

      if (
        (fileWithURL.tag ? fileWithURL.tag.length > 0 : false) ||
        fileWithURL.description
      ) {
        const putObject = {
          description: fileWithURL.description,
          tags: fileWithURL.tag,
        };

        uploaded.description = fileWithURL.description
          ? fileWithURL.description
          : uploaded.description;
        uploaded.tags =
          fileWithURL.tag && fileWithURL.tag.length > 0
            ? fileWithURL.tag
            : uploaded.tags;

        await updateAsset(uploaded.asset_id, putObject);
      }
    }

    dispatch(
      uploadingFilesActions.uploadResultReceived({
        id: upload.id,
        thumbnail: uploaded.thumbnail,
      })
    );
    dispatch(uploadingFilesActions.uploadSucceeded(upload.id));

    // Uploads run fire-and-forget, so a throw here would take a worker with it
    // and must not fall through to the catch below.
    if (!controller.signal.aborted) {
      try {
        onFileUploaded(uploaded, upload.slot);
      } catch (error) {
        console.error("onFileUploaded threw after a successful upload", error);
      }
    }
  } catch (error) {
    if (isUploadAbortedError(error) && controller.signal.aborted) {
      dispatch(clearUploads([upload.id]));
      return;
    }

    dispatch(
      uploadingFilesActions.uploadFailed({
        id: upload.id,
        error: getUploadError(error),
      })
    );
    return;
  } finally {
    abortControllers.delete(upload.id);
  }
}

export function startUploadsToCollection({
  collectionId,
  files,
  onFileUploaded,
  uploadingMode,
  batchId,
}: {
  collectionId: string;
  files: FileToUpload[];
  onFileUploaded(uploaded: CollectionFile, slot: number): void;
  uploadingMode: UploadingMode;
  batchId?: string;
}) {
  return async (dispatch: (action: unknown) => unknown) => {
    const resolvedBatchId = batchId ?? `batch-${getUniqId()}`;
    const uploads = files.map((file, slot) => ({
      id: getUniqId(),
      file,
      slot,
    }));

    for (const upload of uploads) {
      createAbortController(upload.id);
    }

    dispatch(
      uploadingFilesActions.uploadsQueued(
        uploads.map((upload) => ({
          id: upload.id,
          file: upload.file,
          name: upload.file.name,
          type: upload.file.type,
          progress: {
            value: 0,
            status: UploadingStatus.pending,
          },
          upload: {
            loaded: 0,
            total: getFileSize(upload.file),
            value: 0,
          },
          processing: { value: 0 },
          phase: "queued" as UploadPhase,
          batchId: resolvedBatchId,
          collectionId,
        }))
      )
    );

    const runUpload = async (upload: QueuedUpload) => {
      try {
        await uploadSingleFile({
          collectionId,
          upload,
          dispatch,
          onFileUploaded,
        });
      } finally {
        // A failed row keeps its source in the store, which is where Retry
        // reads it from, so the queue can let go either way.
        upload.file = undefined;
      }
    };

    const concurrency =
      uploadingMode === "parallel" ? MAX_CONCURRENT_UPLOADS : 1;

    void runWithConcurrency(uploads, concurrency, runUpload);

    return uploads.map((upload) => upload.id);
  };
}

function getFileSize(file: FileToUpload): number | null {
  return file.contentType === "blob" ? file.blob.size : null;
}

function median(values: number[]): number {
  if (values.length === 0) {
    return 0;
  }

  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);

  return sorted.length % 2 === 0
    ? (sorted[middle - 1] + sorted[middle]) / 2
    : sorted[middle];
}

// Share of the bar given to sending bytes when the batch also has transcoding.
const UPLOAD_SEGMENT_PERCENT = 70;

const BYTES_SENT_PHASES = new Set<UploadPhase>([
  "processing",
  "saving",
  "ready",
]);

export function computeBatchWeights(uploads: UploadingFile[]): {
  bytesDone: number;
  bytesTotal: number;
} {
  const counted = uploads.filter((upload) => upload.phase !== "failed");
  const knownSizes = counted
    .map((upload) => upload.upload.total)
    .filter((total): total is number => typeof total === "number" && total > 0);
  const fallbackWeight = median(knownSizes) || 1;

  return counted.reduce(
    (totals, upload) => {
      const weight = upload.upload.total ?? fallbackWeight;
      const done = BYTES_SENT_PHASES.has(upload.phase)
        ? weight
        : Math.min(weight, (weight * upload.upload.value) / 100);

      return {
        bytesDone: totals.bytesDone + done,
        bytesTotal: totals.bytesTotal + weight,
      };
    },
    { bytesDone: 0, bytesTotal: 0 }
  );
}

export type UploadBatchStage = "idle" | "uploading" | "processing" | "done";

export interface UploadBatchSummary {
  stage: UploadBatchStage;
  total: number;
  uploadedCount: number;
  processedCount: number;
  videosToProcess: number;
  failedCount: number;
  readyCount: number;
  percent: number;
  label: string;
}

export function computeUploadBatchSummary(
  uploads: UploadingFile[]
): UploadBatchSummary {
  const total = uploads.length;
  const failedCount = uploads.filter(
    (upload) => upload.phase === "failed"
  ).length;
  const readyCount = uploads.filter(
    (upload) => upload.phase === "ready"
  ).length;
  const uploadedCount = uploads.filter((upload) =>
    BYTES_SENT_PHASES.has(upload.phase)
  ).length;
  const processingUploads = uploads.filter(
    (upload) => upload.type === "video" && upload.phase !== "failed"
  );
  const videosToProcess = processingUploads.length;
  const processedCount = processingUploads.filter(
    (upload) => upload.phase === "ready"
  ).length;

  if (total === 0) {
    return {
      stage: "idle",
      total,
      uploadedCount,
      processedCount,
      videosToProcess,
      failedCount,
      readyCount,
      percent: 0,
      label: "",
    };
  }

  const isSendingBytes = uploads.some(
    (upload) => upload.phase === "queued" || upload.phase === "uploading"
  );

  // One continuous scale, so the stage boundary never empties the bar.
  const uploadSegment = videosToProcess > 0 ? UPLOAD_SEGMENT_PERCENT : 100;

  if (isSendingBytes) {
    const { bytesDone, bytesTotal } = computeBatchWeights(uploads);
    const percent =
      bytesTotal > 0 ? Math.round((bytesDone / bytesTotal) * uploadSegment) : 0;
    const countable = total - failedCount;

    return {
      stage: "uploading",
      total,
      uploadedCount,
      processedCount,
      videosToProcess,
      failedCount,
      readyCount,
      percent,
      label: `Uploading ${uploadedCount} of ${countable} · ${percent}%`,
    };
  }

  const isProcessing = uploads.some(
    (upload) => upload.phase === "processing" || upload.phase === "saving"
  );

  const hasVideoInFlight = processingUploads.some(
    (upload) => upload.phase === "processing" || upload.phase === "saving"
  );

  if (isProcessing && hasVideoInFlight) {
    const percent = Math.round(
      uploadSegment + (processedCount / videosToProcess) * (100 - uploadSegment)
    );

    return {
      stage: "processing",
      total,
      uploadedCount,
      processedCount,
      videosToProcess,
      failedCount,
      readyCount,
      percent,
      label: `Processing ${processedCount} of ${videosToProcess} ${
        videosToProcess === 1 ? "video" : "videos"
      }`,
    };
  }

  if (isProcessing) {
    return {
      stage: "processing",
      total,
      uploadedCount,
      processedCount,
      videosToProcess,
      failedCount,
      readyCount,
      percent: 100,
      label: "Saving files",
    };
  }

  return {
    stage: "done",
    total,
    uploadedCount,
    processedCount,
    videosToProcess,
    failedCount,
    readyCount,
    percent: 100,
    label: failedCount
      ? `${failedCount} of ${total} files failed`
      : `${readyCount} of ${total} files uploaded`,
  };
}

export function createMonotonicSummarySelector() {
  let lastMembers = "";
  let maxPercent = 0;

  return function selectMonotonicSummary(
    uploads: UploadingFile[]
  ): UploadBatchSummary {
    const summary = computeUploadBatchSummary(uploads);
    // Keyed on which files are in the batch rather than how many: a retry swaps
    // a failed row for a new one and leaves the count untouched.
    const members = uploads.map((upload) => upload.id).join(",");

    if (uploads.length === 0 || members !== lastMembers) {
      lastMembers = members;
      maxPercent = summary.percent;
      return summary;
    }

    maxPercent = Math.max(maxPercent, summary.percent);

    if (maxPercent === summary.percent) {
      return summary;
    }

    return {
      ...summary,
      percent: maxPercent,
      label:
        summary.stage === "uploading"
          ? `Uploading ${summary.uploadedCount} of ${summary.total - summary.failedCount} · ${maxPercent}%`
          : summary.label,
    };
  };
}

export function isUploadComplete(upload: UploadingFile): boolean {
  return (
    upload.progress.status === UploadingStatus.success ||
    upload.progress.status === UploadingStatus.error
  );
}

export type UploadingProgress = "Uploading" | "Transcoding" | null;

export function computeUploadingProgressValue(
  uploads: UploadingFile[]
): number | null {
  const incompleteUploads = uploads.filter(
    (upload) => !isUploadComplete(upload)
  );

  if (incompleteUploads.length === 0) {
    return null;
  }

  const totalProgress = uploads.reduce(
    (sum, upload) => sum + upload.progress.value,
    0
  );

  return Math.round(totalProgress / uploads.length);
}

export function computeUploadingProgress(
  uploads: UploadingFile[]
): UploadingProgress {
  const incompleteUploads = uploads.filter(
    (upload) => !isUploadComplete(upload)
  );

  if (
    incompleteUploads.some(
      (upload) =>
        upload.progress.status === UploadingStatus.pending ||
        upload.progress.status === UploadingStatus.uploading
    )
  ) {
    return "Uploading";
  }

  if (
    incompleteUploads.some(
      (upload) => upload.progress.status === UploadingStatus.transcoding
    )
  ) {
    return "Transcoding";
  }

  return null;
}
